import { skipToken } from '@reduxjs/toolkit/query';
import StatusChip from 'components/@extended/StatusChip';
import MatterIndicators from 'components/matters/MatterIndicators';
import AuditInfo from 'components/audit/AuditInfo';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import { withMissing } from 'components/drafts/withMissing';
import CustomFieldDisplay from 'components/forms/inputs/custom-fields/CustomFieldDisplay';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import GridItemList, { filterRowList, infoLabel, Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { EllipsisTooltip } from 'components/misc/EllipsisTooltip';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useModal } from 'components/modals/useModal';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import cloneDeep from 'lodash/cloneDeep';
import FinancialConditionEditModal from 'pages/financial-conditions/FinancialConditionEditModal';
import { ClientIdentificationPage } from 'pages/client-identification/ClientIdentificationPage';
import { ENTITY_LINK_CLASS, getLinks } from 'pages/matters/util';
import { EditPricelistModal } from 'pages/pricelists/modal/EditPricelistModal';
import { isEmployeePriced } from 'pages/pricelists/modal/employeePricing';
import { PricelistDetailsCard } from 'pages/pricelists/PricelistDetailsCard';
import { Fragment, useCallback, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useGetContractByIdQuery } from 'store/api/contracts';
import { useAddFinancialConditionMutation, useUpdateFinancialConditionMutation } from 'store/api/financial-conditions';
import { useUpdatePricelistMutation } from 'store/api/pricelists';
import { Contract, ContractType } from 'types/contracts';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import {
    FinancialCondition,
    FinancialConditionCreateDto,
    FinancialConditionScopeType,
} from 'types/financial-conditions';
import { Pricelist, PricelistDto } from 'types/pricelists';
import { errorAlert } from 'utils/alert';
import { getOptionsFromClassifier } from 'utils/formUtil';
import { getClassifierText, t } from 'utils/getTranslations';
import { sortByField } from 'utils/sort';
import { useClassifiers } from 'utils/useClassifiers';
import { cn } from 'utils/cn';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { PartyNameParty } from 'utils/partyNameUtil';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { SectionEditButton, SectionEditProvider } from 'components/section-edit/SectionEdit';
import { useLeaveDeletedEntity } from 'utils/useNavigateWithReturnState';
import { ContractEmployeePricelist } from './ContractEmployeePricelist';
import { ContractSection } from './form-sections/ContractSection';
import { ContractPageHeader } from './ContractPageHeader';
import { getAppliedInOfficeContent, getContractParties } from './util';

const ContractPage = (): JSX.Element => {
    const { id: entityId } = useParams();
    const { openModal, closeModal } = useModal();
    const isNew = useIsNewDesign();
    const hasUpdatePermission = useHasPermissions(['UPDATE.CONTRACTS.PARTY_ACCESS']);

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const {
        data: entityData,
        isLoading: isEntityLoading,
        refetch: refetchEntity,
    } = useGetContractByIdQuery(entityId ?? skipToken, { refetchOnMountOrArgChange: true });
    const isDraft = !!entityData?.isDraft;
    // A draft is edited in its form, as a whole, and its pricing is saved with it.
    const canEdit = hasUpdatePermission && !isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/contracts');
    const draftActions = useDraftActions({
        draft: entityData?.isDraft ? { type: 'contract', id: entityData.id } : null,
        createRoute: '/contract/new',
        onDeleted: leaveDeleted,
    });
    const { draft } = draftActions;

    const { customFieldsFiltered, isLoading: isLoadingCustomFieldData } = useCustomFields({
        entityType: EntityType.CONTRACT,
        fields: entityData?.customFields,
    });

    const isInvoicingInfoEnabled = useIsFeatureEnabled(FeatureFlag.INVOICING_INFO_ENABLED);
    const isTimecardIncrementControlEnabled = useIsFeatureEnabled(FeatureFlag.TIMECARD_INCREMENT_CONTROL);
    const isContractTemplateEnabled = useIsFeatureEnabled(FeatureFlag.DOWNLOAD_CONTRACT_TEMPLATE);

    const {
        classifiers,
        isLoading: isLoadingClassifiers,
        allClassifiers,
    } = useClassifiers({
        codes: [
            'DOCUMENT_TEMPLATE_TYPE',
            'CONTRACT_STATUS_TYPE',
            'CONTRACT_TYPE',
            'INVOICING_INTERVAL_TYPE',
            'CURRENCY',
            'TIMECARD_SPECIFICATION_LAYOUT_TYPE',
            'JOB_ROLE_GROUP_TYPE',
            'SCOPE_TYPE',
            'CONTRACT_RELATED_KEYWORD_TYPE',
        ],
    });

    const [addFinancialCondition] = useAddFinancialConditionMutation();
    const [updateFinancialCondition] = useUpdateFinancialConditionMutation();

    const [updatePricelist, { isLoading: isUpdatePricelistLoading }] = useUpdatePricelistMutation();

    const onFinancialConditionFormSumbit = useCallback(
        async (formData: FinancialConditionCreateDto, id?: string): Promise<void> => {
            formData.company = undefined;
            try {
                if (id) {
                    updateFinancialCondition({ id, ...formData })
                        .unwrap()
                        .then(() => {
                            refetchEntity();
                        });
                } else {
                    addFinancialCondition({ ...formData })
                        .unwrap()
                        .then(() => {
                            refetchEntity();
                        });
                }
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addFinancialCondition, refetchEntity, updateFinancialCondition]
    );

    const onPricelistFormSumbit = useCallback(
        async (formData: PricelistDto, entityId?: string): Promise<void> => {
            if (!entityId) {
                return;
            }

            try {
                updatePricelist({ entityId, ...formData })
                    .unwrap()
                    .then(() => {
                        closeModal();
                        refetchEntity();
                    });
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [closeModal, refetchEntity, updatePricelist]
    );

    const openEditFinancialConditionModal = useCallback(
        (editableEntity: FinancialCondition, contract: Contract): void => {
            openModal(
                <FinancialConditionEditModal
                    financialCondition={editableEntity}
                    scopeType={FinancialConditionScopeType.CONTRACT}
                    contract={contract}
                    onChange={(data) => {
                        void onFinancialConditionFormSumbit(
                            data,
                            editableEntity?.id !== undefined ? String(editableEntity?.id) : undefined
                        );
                    }}
                    onClose={closeModal}
                    classifiers={classifiers}
                />,
                closeModal
            );
        },
        [classifiers, openModal, closeModal, onFinancialConditionFormSumbit]
    );

    const openEditPricelistModal = useCallback(
        (pricelist: Pricelist, contractId: number): void => {
            const artModalStyle = { width: 800 };
            openModal(
                <EditPricelistModal
                    entityId={pricelist.id}
                    scopeType="CONTRACT"
                    contractId={contractId}
                    onFormSumbit={onPricelistFormSumbit}
                    onClose={closeModal}
                    classifiers={classifiers}
                />,
                closeModal,
                artModalStyle
            );
        },
        [classifiers, openModal, closeModal, onPricelistFormSumbit]
    );

    // One set of rows for both designs: classic lists them in its General and Financial cards, the new design
    // in the cards whose section edits them.
    const rows = useMemo(() => {
        const isServiceAgreement = entityData?.contractTypeCode === ContractType.SERVICE_AGREEMENT;
        const hasAdminFeePermitted =
            isServiceAgreement &&
            entityData?.contractRelatedKeywords?.some((kw) => kw.contractRelatedKeywordCode === 'ADMIN_FEE_PERMITTED');
        return {
            type: {
                content: getClassifierText(classifiers['CONTRACT_TYPE'], entityData?.contractTypeCode) || '-',
                label: t('contracts.type'),
            },
            nameExtension: isServiceAgreement
                ? {
                      content: entityData?.nameExtension || '-',
                      label: t('contracts.name-extension'),
                  }
                : null,
            clientTemplate: {
                content: entityData?.isClientTemplate ? t('yes') : t('no'),
                label: t('contracts.client-template'),
            },
            documentTemplate: {
                content: entityData?.contractTemplate?.name || '-',
                label: t('contracts.document-template'),
            },
            concludedBy: {
                content: entityData?.tenantCompany?.name || '-',
                label: t('contracts.concluded-by'),
            },
            connectedContacts: {
                content: entityData?.parties ? getContractParties(cloneDeep(entityData.parties)) : '-',
                label: t('contracts.connected-contacts'),
            },
            signedBy: {
                content: entityData?.signedByPersonParty ? (
                    <PartyName
                        party={entityData.signedByPersonParty}
                        isLink
                        variant="readonly-other"
                        className="whitespace-nowrap"
                    />
                ) : (
                    '-'
                ),
                label: t('contracts.signed-by-client-contact'),
            },
            contractNumber: {
                content: entityData?.contractNumber || '-',
                label: t('contracts.number'),
            },
            keywords: {
                content: (
                    <span className="flex flex-row flex-wrap gap-1">
                        {(entityData?.contractRelatedKeywords || []).length > 0
                            ? entityData?.contractRelatedKeywords?.map((keyword) => (
                                  <StatusChip
                                      key={keyword.id}
                                      label={getClassifierText(
                                          classifiers['CONTRACT_RELATED_KEYWORD_TYPE'],
                                          keyword.contractRelatedKeywordCode
                                      )}
                                      type="neutral"
                                  />
                              ))
                            : '-'}
                    </span>
                ),
                label: t('contracts.contract-related-keywords'),
            },
            dmsLink: {
                content: entityData?.dmsLink ? (
                    <a className={ENTITY_LINK_CLASS} href={entityData.dmsLink}>
                        {entityData.dmsLink}
                    </a>
                ) : (
                    '-'
                ),
                label: t('contracts.dms-link'),
            },
            appliedIn: {
                content: entityData ? getAppliedInOfficeContent(entityData) : '-',
                label: t('contracts.applied-in'),
            },
            paymentDeadline: {
                content: entityData?.financialCondition?.paymentDeadlineInDays?.toString() || '-',
                label: t('financial-conditions.payment-deadline'),
            },
            prepaymentDeadline: {
                content: entityData?.financialCondition?.prepaymentPaymentDeadlineInDays?.toString() || '-',
                label: t('financial-conditions.prepayment-payment-deadline'),
            },
            invoicingInterval: {
                content:
                    getClassifierText(
                        classifiers['INVOICING_INTERVAL_TYPE'],
                        entityData?.financialCondition?.invoicingIntervalTypeCode
                    ) || '-',
                label: t('financial-conditions.invoicing-interval'),
            },
            discount: {
                content: entityData?.discountPct || '-',
                label: t('matters.discount'),
            },
            adminFee: hasAdminFeePermitted
                ? {
                      content: entityData?.adminFeePercentage || '-',
                      label: t('contracts.admin-fee-percentage'),
                  }
                : null,
            interestRate: {
                content: entityData?.financialCondition?.interestRatePct || '-',
                label: t('financial-conditions.interest-rate'),
            },
            interestRateB2c: {
                content: entityData?.financialCondition?.interestRateB2cPct || '-',
                label: t('financial-conditions.interest-rate-b2c'),
            },
            currency: {
                content:
                    getClassifierText(classifiers['CURRENCY'], entityData?.financialCondition?.currencyTypeCode) || '-',
                label: t('currency'),
            },
            timecardIncrement:
                isTimecardIncrementControlEnabled && isServiceAgreement
                    ? {
                          content: entityData?.timecardIncrement?.toString() || '-',
                          label: t('contracts.timecard-increment'),
                      }
                    : null,
            iban: {
                content: entityData?.bankAccount?.name || '-',
                label: t('financial-conditions.company.iban'),
            },
        } satisfies Record<string, Row | null>;
    }, [entityData, classifiers, isTimecardIncrementControlEnabled]);

    const getGeneralData = (): Row[] =>
        filterRowList([
            rows.type,
            rows.nameExtension,
            rows.clientTemplate,
            rows.documentTemplate,
            rows.concludedBy,
            rows.connectedContacts,
            rows.signedBy,
            rows.contractNumber,
            rows.keywords,
            rows.dmsLink,
            rows.appliedIn,
        ]);

    const getNoteslData = useCallback(
        (): Row[] => [
            {
                content: entityData?.additionalNotes || '-',
                label: t('contracts.additional-notes'),
                multiline: true,
            },
        ],
        [entityData]
    );

    const getBillingData = useCallback(
        (): Row[] => [
            { content: entityData?.billingNotes || '-', label: t('contracts.billing-notes'), multiline: true },
            ...(isInvoicingInfoEnabled
                ? [
                      {
                          content: entityData?.invoicingInfo || '-',
                          label: t('contracts.invoicing-info-internal'),
                          multiline: true,
                      },
                  ]
                : []),
            {
                content: entityData?.clientReference || '-',
                label: isNew
                    ? infoLabel('contracts.client-reference', 'contracts.tooltips.client-reference')
                    : t('contracts.client-reference'),
            },
        ],
        [entityData, isInvoicingInfoEnabled, isNew]
    );

    const getUltimateBeneficialOwnersData = useCallback(
        (): Row[] => [
            {
                content: getLinks('contact', entityData?.beneficialOwners || [], { renderMethod: 'vertical' }),
                label: t('contracts.ultimate-beneficial-owners'),
            },
            {
                content: entityData?.beneficialOwnerNotes || '-',
                label: t('contracts.beneficial-owner-notes'),
            },
        ],
        [entityData]
    );

    const getConnectedCasesData = useCallback(
        (): Row[] => [
            {
                content: (
                    <div className="flex flex-col">
                        {(entityData?.connectedMatters?.length ?? 0) > 0
                            ? entityData?.connectedMatters?.map((matter) => {
                                  return (
                                      <Fragment key={matter.matterId}>
                                          <span className="flex items-center gap-1">
                                              <MatterIndicators isConfidential={matter.isConfidential} />
                                              <RecordLink
                                                  className={cn(ENTITY_LINK_CLASS, 'whitespace-nowrap')}
                                                  to={`/matter/${matter.matterId}`}
                                                  label={`(${matter.matterNumber}) ${matter.matterName}`}
                                              />
                                          </span>
                                      </Fragment>
                                  );
                              })
                            : '-'}
                    </div>
                ),
                label: t('contracts.connected-cases'),
            },
        ],
        [entityData]
    );

    const getDates = useCallback(
        (): Row[] => [
            {
                content: <FormattedDate value={entityData?.createdAt} />,
                label: t('contracts.created'),
            },
            {
                content: <FormattedDate value={entityData?.contractSentAt} />,
                label: t('contracts.sent'),
            },
            {
                content: <FormattedDate value={entityData?.contractSignedAt} />,
                label: t('contracts.signed'),
            },
            {
                content: <FormattedDate value={entityData?.contractTerminatedAt} />,
                label: t('contracts.terminated'),
            },
        ],
        [entityData]
    );

    const getFinancialData = (): Row[] =>
        filterRowList([
            rows.paymentDeadline,
            rows.prepaymentDeadline,
            rows.invoicingInterval,
            rows.discount,
            rows.adminFee,
            rows.interestRate,
            rows.interestRateB2c,
            rows.currency,
            rows.timecardIncrement,
            rows.iban,
        ]);

    // An employee-priced list draws its own actions: it follows a model or keeps its own prices. They stay with the
    // flag off, which stops only new employee-priced lists.
    const employeePricelist = isEmployeePriced(entityData?.pricelist) ? entityData?.pricelist : undefined;

    const getPricelistInfo = (): Row[] => [
        {
            content:
                employeePricelist && entityData ? (
                    <ContractEmployeePricelist
                        contractId={entityData.id}
                        pricelist={employeePricelist}
                        canEdit={canEdit}
                        onEdit={() => openEditPricelistModal(employeePricelist, entityData.id)}
                        onChanged={refetchEntity}
                    />
                ) : (
                    <PricelistDetailsCard pricelist={entityData?.pricelist} />
                ),
            label: '',
        },
    ];

    const editFinancialConditionButton = useCallback(
        (contract: Contract): JSX.Element => (
            <PermissionButton
                permissions={['UPDATE.CONTRACTS.PARTY_ACCESS']}
                variant="outlined"
                size="extraSmall"
                onClick={() => openEditFinancialConditionModal(contract.financialCondition, contract)}
            >
                <FormattedMessage id="edit" />
            </PermissionButton>
        ),
        [openEditFinancialConditionModal]
    );

    const editPricelistButton = useCallback(
        (contract: Contract): JSX.Element =>
            contract.pricelist && !employeePricelist ? (
                <PermissionButton
                    permissions={['UPDATE.CONTRACTS.PARTY_ACCESS']}
                    variant="outlined"
                    size="extraSmall"
                    onClick={() => openEditPricelistModal(contract.pricelist!, contract.id)}
                >
                    <FormattedMessage id="edit" />
                </PermissionButton>
            ) : (
                <></>
            ),
        [employeePricelist, openEditPricelistModal]
    );

    const customFieldData = useMemo<Row[]>(
        () =>
            customFieldsFiltered.map((field) => {
                return {
                    label: field.label,
                    content: <CustomFieldDisplay key={field.id} field={field} classifiers={allClassifiers} />,
                };
            }),
        [allClassifiers, customFieldsFiltered]
    );

    if (isEntityLoading || isLoadingClassifiers || isUpdatePricelistLoading || isLoadingCustomFieldData) {
        return <Loader />;
    }

    if (entityData?.contractTypeCode === ContractType.CLIENT_IDENTIFICATION) {
        return <ClientIdentificationPage contract={entityData} />;
    }

    const isAmlOrUbo =
        entityData?.contractTypeCode === ContractType.AML || entityData?.contractTypeCode === ContractType.UBO;
    const isRepresentation = entityData?.contractTypeCode === ContractType.REPRESENTATION;
    // A draft's empty required values read as missing, wherever their rows are.
    const missing = (data: Row[]) => withMissing(data, draft?.missingFields);
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;
    // Whom a draft is shared with heads the right column; a draft lists no connected cases yet.
    const sharedWith = isDraft ? (
        <DraftActionsContext.Provider value={draftActions}>
            <DraftSharedWith />
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    ) : null;

    // The new design edits each card in place, saving only its fields; the price list and the financial conditions
    // keep their modals. Each card lists its form's fields in the form's order, under the form's labels and where
    // the form shows them, so a row and its field share a cell.
    if (isNew && entityData) {
        const hasPricing = !isAmlOrUbo && !isRepresentation;
        const isCooperation = entityData.contractTypeCode === ContractType.COOPERATION;
        const modalEdit = (onClick: () => void) => (canEdit ? <SectionEditButton onClick={onClick} /> : null);
        const contactChips = (parties: PartyNameParty[]) =>
            parties.map((party) => ({
                key: party.id ?? party.name,
                label: <PartyName party={party} isLink variant="readonly-other" />,
                record: !!party.id,
            }));
        // A service agreement that stores no increment is asked for one by every card but General, whose form has
        // the field anyway.
        const offeredIncrement: Row = {
            content: '-',
            label: t('contracts.timecard-increment'),
            hidden:
                !isTimecardIncrementControlEnabled ||
                entityData.contractTypeCode !== ContractType.SERVICE_AGREEMENT ||
                entityData.timecardIncrement != null,
        };
        const keywordCodes = entityData.contractRelatedKeywords?.map((kw) => kw.contractRelatedKeywordCode) ?? [];
        // The form leaves the type's terms and the bank account out of a cooperation contract: no field edits them.
        // The Contract card's form can change the type, and then draws the terms: the discount's row leaves for it.
        const staysCooperation = ({ contractType }: FieldValues) => contractType?.value === ContractType.COOPERATION;
        // A row whose field has an info icon carries it, placed as the field has it, so its label wraps alike.
        const contractRows = filterRowList([
            rows.type,
            rows.nameExtension && {
                ...rows.nameExtension,
                label: infoLabel('contracts.name-extension', 'contracts.tooltips.name-extension'),
            },
            { ...rows.clientTemplate, hidden: !isContractTemplateEnabled },
            { ...rows.documentTemplate, label: t('contracts.contract-template'), hidden: !isContractTemplateEnabled },
            { ...rows.contractNumber, label: infoLabel('contracts.number', 'contracts.tooltips.contract-number') },
            {
                ...rows.keywords,
                content: '-',
                // In the classifier's order, as the form's value is.
                chips: getOptionsFromClassifier<string, string>(classifiers['CONTRACT_RELATED_KEYWORD_TYPE'])
                    .filter(({ value }) => keywordCodes.includes(value))
                    .map(({ value, label }) => ({ key: value, label })),
            },
            rows.dmsLink,
            ...(hasPricing
                ? [
                      rows.timecardIncrement,
                      rows.adminFee,
                      { ...rows.discount, readOnly: isCooperation && staysCooperation },
                  ]
                : []),
        ]);
        const partiesRows = filterRowList([
            {
                ...rows.connectedContacts,
                label: infoLabel('contracts.connected-contacts', 'contracts.tooltips.connected-contacts'),
                content: '-',
                chips: contactChips((entityData.parties ?? []).map(({ party }) => party)),
            },
            { ...rows.concludedBy, label: infoLabel('contracts.concluded-by', 'contracts.tooltips.concluded-by') },
            {
                ...rows.appliedIn,
                label: infoLabel('contracts.applied-in', 'contracts.tooltips.applied-in'),
                content: '-',
                // The companies alone, as the field holds them; the record's header lists their offices.
                chips: (entityData.companies ?? []).map(({ company }) => ({ key: company.id, label: company.name })),
            },
            {
                content: entityData.signedByPersonParty ? (
                    <EllipsisTooltip>
                        <PartyName
                            party={entityData.signedByPersonParty}
                            isLink
                            variant="readonly-other"
                            singleLine
                            hideTitle
                        />
                    </EllipsisTooltip>
                ) : (
                    '-'
                ),
                label: t(isCooperation ? 'contracts.signed-by-contact-person' : 'contracts.signed-by-client-contact'),
            },
            hasPricing ? { ...rows.iban, label: t('contracts.bank-account'), readOnly: isCooperation } : null,
            offeredIncrement,
        ]);
        // No field edits the creation date: it follows the dates a field does.
        const [createdAt, ...editedDates] = getDates();
        const datesRows = [...editedDates, offeredIncrement, { ...createdAt, readOnly: true }];
        return (
            <PaddedLayout>
                <ContractPageHeader contract={entityData} classifiers={classifiers} draftActions={draftActions} />
                {draftNotice ? <div className="mb-(--card-gap)">{draftNotice}</div> : null}
                <SectionEditProvider canEdit={canEdit}>
                    <div className="grid grid-cols-1 items-start gap-(--card-gap) md:grid-cols-2">
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            <ContractSection
                                id="contract"
                                titleId="contracts.general"
                                contract={entityData}
                                rows={contractRows}
                            >
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="contracts.general"
                                        button={edit}
                                        data={missing(contractRows)}
                                    />
                                )}
                            </ContractSection>
                            <ContractSection
                                id="parties"
                                titleId="contracts.parties-and-company"
                                contract={entityData}
                                rows={partiesRows}
                            >
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="contracts.parties-and-company"
                                        button={edit}
                                        data={missing(partiesRows)}
                                    />
                                )}
                            </ContractSection>
                            {isAmlOrUbo ? (
                                <ContractSection
                                    id="beneficial-owners"
                                    titleId="contracts.ultimate-beneficial-owners"
                                    contract={entityData}
                                >
                                    {(edit) => (
                                        <GridItemList
                                            isNew
                                            titleId="contracts.ultimate-beneficial-owners"
                                            button={edit}
                                            data={[
                                                {
                                                    content: '-',
                                                    label: t('contracts.ultimate-beneficial-owners'),
                                                    chips: contactChips(
                                                        sortByField(entityData.beneficialOwners ?? [], 'name')
                                                    ),
                                                },
                                                {
                                                    content: entityData.beneficialOwnerNotes || '-',
                                                    label: t('contracts.beneficial-owner-notes'),
                                                    multiline: true,
                                                },
                                            ]}
                                        />
                                    )}
                                </ContractSection>
                            ) : null}
                            {hasPricing ? (
                                <ContractSection
                                    id="billing"
                                    titleId="contracts.billing.title"
                                    contract={entityData}
                                    canEdit={!isCooperation}
                                >
                                    {(edit) => (
                                        <GridItemList
                                            isNew
                                            titleId="contracts.billing.title"
                                            button={edit}
                                            data={missing([...getBillingData(), offeredIncrement])}
                                        />
                                    )}
                                </ContractSection>
                            ) : null}
                            <ContractSection id="notes" titleId="contracts.notes" contract={entityData}>
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="contracts.notes"
                                        button={edit}
                                        data={missing([...getNoteslData(), offeredIncrement])}
                                    />
                                )}
                            </ContractSection>
                            {isDraft ? null : (
                                <GridItemList
                                    isNew
                                    titleId="contracts.connected-cases"
                                    data={getConnectedCasesData()}
                                />
                            )}
                            <AuditInfo
                                createdAt={entityData.createdAt}
                                createdByEmployee={entityData.createdByEmployee}
                                lastModifiedAt={entityData.lastModifiedAt}
                                changedByEmployee={entityData.changedByEmployee}
                            />
                        </div>
                        <div className="flex min-w-0 flex-col gap-(--card-gap)">
                            {sharedWith}
                            {hasPricing ? (
                                <>
                                    <GridItemList
                                        isNew
                                        titleId="contracts.pricelist"
                                        button={
                                            entityData.pricelist && !employeePricelist
                                                ? modalEdit(() =>
                                                      openEditPricelistModal(entityData.pricelist!, entityData.id)
                                                  )
                                                : null
                                        }
                                        data={getPricelistInfo().map((row) => ({ ...row, wide: true }))}
                                    />
                                    <GridItemList
                                        isNew
                                        titleId="contracts.financial"
                                        button={modalEdit(() =>
                                            openEditFinancialConditionModal(entityData.financialCondition, entityData)
                                        )}
                                        data={missing(
                                            filterRowList([
                                                rows.paymentDeadline,
                                                rows.prepaymentDeadline,
                                                rows.invoicingInterval,
                                                rows.interestRate,
                                                rows.interestRateB2c,
                                                rows.currency,
                                            ])
                                        )}
                                    />
                                </>
                            ) : null}
                            <ContractSection
                                id="dates"
                                titleId="contracts.dates"
                                contract={entityData}
                                rows={datesRows}
                            >
                                {(edit) => (
                                    <GridItemList
                                        isNew
                                        titleId="contracts.dates"
                                        button={edit}
                                        data={missing(datesRows)}
                                    />
                                )}
                            </ContractSection>
                            {customFieldData.length > 0 ? (
                                <ContractSection id="custom-fields" titleId="custom-fields.title" contract={entityData}>
                                    {(edit) => (
                                        <GridItemList
                                            isNew
                                            titleId="custom-fields.title"
                                            button={edit}
                                            data={missing(customFieldData)}
                                        />
                                    )}
                                </ContractSection>
                            ) : null}
                        </div>
                    </div>
                </SectionEditProvider>
            </PaddedLayout>
        );
    }

    return entityData ? (
        <PaddedLayout>
            <ContractPageHeader contract={entityData} classifiers={classifiers} draftActions={draftActions} />
            {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
            <div className="grid grid-cols-1 items-start gap-6 sm:grid-cols-2">
                <div>
                    <div className="flex flex-col gap-6">
                        <GridItemList
                            titleId="contracts.general"
                            size={12}
                            data={missing(getGeneralData())}
                            key="contract-general"
                        />
                        <GridItemList
                            titleId="contracts.notes"
                            size={12}
                            data={missing(getNoteslData())}
                            key="contract-notes"
                        />
                        {isAmlOrUbo ? (
                            <GridItemList
                                titleId="contracts.ultimate-beneficial-owners"
                                size={12}
                                data={getUltimateBeneficialOwnersData()}
                                key="contract-ultimate-beneficial-owners"
                            />
                        ) : null}
                        {!isAmlOrUbo && !isRepresentation ? (
                            <GridItemList
                                titleId="contracts.billing.title"
                                size={12}
                                data={missing(getBillingData())}
                                key="contract-billing"
                            />
                        ) : null}
                        {isDraft ? null : (
                            <GridItemList
                                titleId="contracts.connected-cases"
                                size={12}
                                data={getConnectedCasesData()}
                                key="contract-connected-cases"
                            />
                        )}
                    </div>
                    <div className="mt-4">
                        <AuditInfo
                            createdAt={entityData.createdAt}
                            createdByEmployee={entityData.createdByEmployee}
                            lastModifiedAt={entityData.lastModifiedAt}
                            changedByEmployee={entityData.changedByEmployee}
                        />
                    </div>
                </div>
                <div className="flex flex-col gap-6">
                    {sharedWith}
                    {!isAmlOrUbo && !isRepresentation ? (
                        <>
                            <GridItemList
                                button={isDraft ? undefined : editPricelistButton(entityData)}
                                titleId="contracts.pricelist"
                                size={12}
                                data={getPricelistInfo()}
                                key="contract-pricelist"
                            />
                            <GridItemList
                                button={isDraft ? undefined : editFinancialConditionButton(entityData)}
                                titleId="contracts.financial"
                                size={12}
                                data={missing(getFinancialData())}
                                key="contract-financial"
                            />
                        </>
                    ) : null}
                    <GridItemList titleId="contracts.dates" size={12} data={missing(getDates())} key="contract-dates" />
                    <GridItemList
                        titleId="custom-fields.title"
                        size={12}
                        data={missing(customFieldData)}
                        key="contract-custom-fields"
                    />
                </div>
            </div>
        </PaddedLayout>
    ) : (
        <></>
    );
};

export default ContractPage;
